import { MESSAGES } from '../config.js';
import { clear, el } from './dom.js';

/** Static screens — intro, flip instruction (§5), upload progress (§21), results. */

export function showScreen(root: HTMLElement, node: HTMLElement): void {
  clear(root);
  root.append(node);
}

export function introScreen(onStart: () => void): HTMLElement {
  const btn = el('button', { class: 'btn', text: 'Start' });
  btn.addEventListener('click', onStart);
  return el('div', { class: 'screen' }, [
    el('h1', { text: 'Identity Verification' }),
    el('p', {
      text:
        'You will record 3 short videos: the front of your licence, the back of your licence, ' +
        'and a selfie video. Use good lighting and hold your phone still.',
    }),
    el('p', { text: 'You will be asked for camera permission.' }),
    btn,
  ]);
}

export function flipScreen(onContinue: () => void): HTMLElement {
  const btn = el('button', { class: 'btn', text: 'FLIP THE CARD' });
  btn.addEventListener('click', onContinue);
  return el('div', { class: 'screen' }, [
    el('div', { class: 'badge ok', text: 'FRONT COMPLETE ✓' }),
    el('h1', { text: 'Flip the card' }),
    el('p', { text: MESSAGES.flipCard }),
    el('p', { text: 'Place the back side inside the frame — all four corners must be visible.' }),
    btn,
  ]);
}

export interface UploadRowRefs {
  setState(state: string, tone?: 'ok' | 'err' | 'muted'): void;
  setProgress(ratio: number): void;
}

export function uploadScreen(stages: Array<{ stage: string; label: string }>): {
  node: HTMLElement;
  rows: Map<string, UploadRowRefs>;
} {
  const rows = new Map<string, UploadRowRefs>();
  const list = el('div', { class: 'upload-list' });
  for (const s of stages) {
    const state = el('span', { class: 'state', text: 'Waiting…' });
    const fill = el('div');
    const bar = el('div', { class: 'progress' }, [fill]);
    list.append(el('div', { class: 'upload-row' }, [el('div', { class: 'label' }, [el('span', { text: s.label }), state]), bar]));
    rows.set(s.stage, {
      setState: (t, tone = 'muted') => {
        state.textContent = t;
        state.className = `state${tone === 'ok' ? ' ok' : tone === 'err' ? ' err' : ''}`;
      },
      setProgress: (r) => (fill.style.width = `${Math.round(r * 100)}%`),
    });
  }
  const node = el('div', { class: 'screen' }, [
    el('h1', { text: 'Uploading your videos' }),
    el('p', { text: 'Please keep this page open until the upload completes.' }),
    list,
  ]);
  return { node, rows };
}

export function resultScreen(
  kind: 'submitted' | 'approved' | 'rejected' | 'expired' | 'error',
  detail?: string,
): HTMLElement {
  const content: Record<string, { badge: string; tone: 'ok' | 'err' | 'warn'; title: string; body: string }> = {
    submitted: {
      badge: 'SUBMITTED',
      tone: 'ok',
      title: 'Verification submitted',
      body: 'Your videos have been sent for review. You can close this page now.',
    },
    approved: { badge: 'APPROVED', tone: 'ok', title: 'Verification successful', body: 'Your identity has been verified.' },
    rejected: { badge: 'REJECTED', tone: 'err', title: 'Verification declined', body: detail ?? 'Please contact support for more information.' },
    expired: { badge: 'EXPIRED', tone: 'warn', title: 'Link expired', body: 'Please request a new verification link.' },
    error: { badge: 'ERROR', tone: 'err', title: 'Something went wrong', body: detail ?? 'Please try again or contact support.' },
  };
  const c = content[kind]!;
  return el('div', { class: 'screen' }, [
    el('div', { class: `badge ${c.tone}`, text: c.badge }),
    el('h1', { text: c.title }),
    el('p', { text: c.body }),
  ]);
}

export function rejectionScreen(stageLabel: string, reasons: string[], onRetake: () => void): HTMLElement {
  const btn = el('button', { class: 'btn', text: 'Record again' });
  btn.addEventListener('click', onRetake);
  return el('div', { class: 'screen' }, [
    el('div', { class: 'badge err', text: 'RETAKE REQUIRED' }),
    el('h1', { text: `${stageLabel} was not accepted` }),
    el('p', { class: 'reasons', text: reasons.join(' • ') || 'Quality was not sufficient' }),
    el('p', { text: 'Try again in better lighting, holding the phone completely still.' }),
    btn,
  ]);
}

export function loadingScreen(text: string): HTMLElement {
  return el('div', { class: 'screen' }, [el('h1', { text })]);
}

/** §25 — recoverable failure (e.g. network during upload): keep state, let the user retry. */
export function retryScreen(message: string, onRetry: () => void): HTMLElement {
  const btn = el('button', { class: 'btn', text: 'Try again' });
  btn.addEventListener('click', onRetry);
  return el('div', { class: 'screen' }, [
    el('div', { class: 'badge warn', text: 'RETRY' }),
    el('h1', { text: 'Something went wrong' }),
    el('p', { text: message }),
    btn,
  ]);
}
